import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import logo_ligne_couleur_002_2x from '../assets/images/logo_ligne_couleur_002@2x.png'
import instagramIcon from '../assets/images/instagram.png'
import supabase from '../utils/supabaseClient';

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ text: string; success: boolean } | null>(null);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setMessage(null);

    const { error } = await supabase.from('newsletter').insert([{ email }]);

    if (error) {
      if (error.code === '23505') {
        setMessage({ text: 'Vous êtes déjà inscrit !', success: false });
      } else {
        setMessage({ text: 'Une erreur est survenue. Veuillez réessayer.', success: false });
      }
    } else {
      setMessage({ text: 'Merci pour votre inscription !', success: true });
      setEmail('');
    }
    setSubmitting(false);
  };

  return (
    <footer className="bg-slate-800 text-white">
      <div className="py-8 px-4 sm:px-8 md:px-16 lg:px-24">
        <div className="flex flex-col md:flex-row justify-between md:items-start gap-6">
          {/* Logo et description */}
          <div className="flex-1 flex flex-col items-center md:items-start text-center md:text-left">
            <div>
              <img src={logo_ligne_couleur_002_2x} alt="Logo" className="h-auto w-auto max-w-full max-h-28" />
            </div>
            <p className="text-gray-300 mb-4 text-base leading-relaxed">
              Location de van aménagé pour des aventures inoubliables en toute liberté.
              Partez à l'aventure avec confort et style.
            </p>
            <div className="flex items-center gap-4 justify-center md:justify-start">
              <p className='text-gray-300 text-base leading-relaxed font-bold'>Nous suivre :</p>
              
              {/* Social Media Icons */}
              <div className="flex space-x-3">
                <a href="https://instagram.com/2.the.road" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-blue-500 transition-colors" aria-label="Instagram">
                  <img src={instagramIcon} alt="Instagram"/>
                </a>
                {/* Ajoutez d'autres icônes de réseaux sociaux ici */}
              </div>
            </div>
          </div>

          {/* Informations */}
          <div className="flex-1 flex flex-col items-center">
            <h4 className="font-semibold text-white mb-6">Informations</h4>
            <ul className="space-y-3 text-center">
              <li>
                <Link to="/a-propos" className="text-gray-300 hover:text-blue-400 transition-colors text-base">
                  A propos
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-300 hover:text-blue-400 transition-colors text-base">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-blue-400 transition-colors text-base">
                  Nous contacter
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="flex-1 flex flex-col items-stretch md:items-start">
            <div className="bg-slate-700 rounded-xl p-6 border border-slate-600 w-full">
              <div className="flex items-center mb-4">
                <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <h4 className="font-semibold text-white text-lg">Newsletter</h4>
              </div>
              <p className="text-gray-300 mb-4 text-base leading-relaxed">
                Recevez nos dernières aventures et offres exclusives directement dans votre boîte mail !
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre adresse email"
                  className="flex-1 px-4 py-3 rounded-lg bg-slate-600 border border-slate-500 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full sm:w-auto text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 text-sm whitespace-nowrap ${submitting ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
                >
                  {submitting ? '...' : "S'abonner"}
                </button>
              </form>
              {message && (
                <p className={`mt-2 text-sm ${message.success ? 'text-green-400' : 'text-red-400'}`}>
                  {message.text}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-slate-700 mt-8 pt-6 text-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} To The Road. Tous droits réservés.
          </p>
          {/* Mentions légales et Politique de confidentialité, Cookies, Conditions générales de location*/}
          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2">
            <Link to="/mentions-legales" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
              Mentions légales
            </Link>
            <span className="text-gray-400">|</span>
            <Link to="/politique-confidentialite" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
              Politique de confidentialité
            </Link>
            <span className="text-gray-400">|</span>
            <Link to="/cookies" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
              Cookies
            </Link>
            <span className="text-gray-400">|</span>
            <Link to="/conditions-generales" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
              Conditions générales d'utilisation
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;